import { router } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { goalIconOf } from '@/components/goal-icon-picker';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ProgressRing } from '@/components/ui/progress-ring';
import { savedGrOf, totalSavingsGrOf, useGoalsStore } from '@/store/goals-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatPln } from '@/utils/format';

/** Savings goals — mock: lista_cel_w/screen.png; rings never start at 0% */
export default function GoalsScreen() {
  const { t } = useTranslation();
  const goals = useGoalsStore((s) => s.goals);
  const contributions = useGoalsStore((s) => s.contributions);

  const contributedThisWeek = contributions.some(
    (c) => Date.now() - new Date(c.createdAt).getTime() < 7 * 86_400_000,
  );
  const contributedThisMonthGr = contributions
    .filter((c) => c.date.slice(0, 7) === new Date().toISOString().slice(0, 7))
    .reduce((sum, c) => sum + c.amountGr, 0);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <AppText variant="headlineLg" color={colors.navy}>
          {t('goals.title')}
        </AppText>

        {/* Total savings hero */}
        <View
          style={{
            backgroundColor: colors.primary,
            borderRadius: radius.card,
            padding: spacing.screen,
            gap: spacing.base,
          }}
        >
          <AppText variant="labelLg" color="#DCE9FF">
            {t('goals.totalSavings')}
          </AppText>
          <AppText variant="headlineLg" color="#FFFFFF" style={{ fontSize: 36, lineHeight: 42 }}>
            {formatPln(totalSavingsGrOf(goals, contributions))}
          </AppText>
        </View>

        {/* Streak chip — social proof of momentum */}
        {contributedThisWeek && (
          <View style={{ alignItems: 'flex-end' }}>
            <View
              style={{
                backgroundColor: '#FFF3C4',
                borderRadius: radius.full,
                paddingHorizontal: spacing.md,
                paddingVertical: spacing.base,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                <Ionicons name="flame" size={16} color="#8A6D00" />
                <AppText variant="labelLg" color="#8A6D00">
                  {t('goals.streak')}
                </AppText>
              </View>
            </View>
          </View>
        )}

        <View style={{ gap: spacing.md }}>
          {goals.map((goal) => {
            const savedGr = savedGrOf(goal, contributions);
            const percent = Math.min(100, Math.round((savedGr / goal.targetGr) * 100));
            return (
              <Pressable
                key={goal.id}
                accessibilityRole="button"
                onPress={() => router.push({ pathname: '/goal/[id]', params: { id: goal.id } })}
                style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
              >
                <Card style={{ gap: spacing.sm }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
                    <ProgressRing percent={percent} size={64} strokeWidth={7}>
                      <AppText variant="labelLg" color={colors.navy}>
                        {percent}%
                      </AppText>
                    </ProgressRing>
                    <View style={{ flex: 1, gap: 2 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                        <Ionicons name={goalIconOf(goal)} size={18} color={colors.primary} />
                        <AppText variant="headlineSm">{goal.name}</AppText>
                      </View>
                      <AppText variant="bodyMd">
                        {formatPln(savedGr)} / {formatPln(goal.targetGr)} · {percent}%
                      </AppText>
                    </View>
                    <Ionicons name="chevron-forward" size={20} color={colors.outlineVariant} />
                  </View>
                  <View style={{ height: 8, borderRadius: radius.full, backgroundColor: colors.primaryTile, overflow: 'hidden' }}>
                    <View style={{ height: 8, width: `${percent}%`, borderRadius: radius.full, backgroundColor: colors.navy }} />
                  </View>
                </Card>
              </Pressable>
            );
          })}
        </View>

        {/* Advisor tip */}
        <View
          style={{
            flexDirection: 'row',
            gap: spacing.sm,
            backgroundColor: colors.infoBg,
            borderRadius: radius.card,
            padding: spacing.md,
          }}
        >
          <Ionicons name="bulb-outline" size={22} color={colors.navy} />
          <View style={{ flex: 1, gap: 2 }}>
            <AppText variant="labelLg" color={colors.navy}>
              {t('goals.tipTitle')}
            </AppText>
            <AppText variant="bodyMd">
              {contributedThisMonthGr > 0
                ? t('goals.tipProgress', { amount: formatPln(contributedThisMonthGr) })
                : t('goals.tipStart')}
            </AppText>
          </View>
        </View>

        <View style={{ alignItems: 'flex-end' }}>
          <PrimaryButton
            label={`+ ${t('goals.newGoal')}`}
            onPress={() => router.push('/goal/new')}
            style={{ backgroundColor: colors.navy, borderRadius: radius.full, paddingHorizontal: spacing.xl }}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
